/**
 * Threat tests panel: list test runs, filter active/completed,
 * run check, cancel, cleanup per test, and show linked threat events.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../app/ToastProvider';
import {
  ArrowLeft,
  FlaskConical,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Trash2,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Shield,
} from 'lucide-react';
import type { ArbitiumTest, ArbitiumThreatEvent } from '../types/tests';
import {
  getTestData,
  checkTest,
  abortTest,
  cleanupTest,
  getThreatEventsByCandidateId,
} from '../api-utils/tests';

type FilterMode = 'active' | 'completed';

interface ThreatTestsPanelProps {
  onBack: () => void;
}

function formatIsoDate(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export const ThreatTestsPanel: React.FC<ThreatTestsPanelProps> = ({ onBack }) => {
  const { notify } = useToast();
  const notifyError = (message: string) => notify({ tone: 'error', message });
  const [tests, setTests] = useState<ArbitiumTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>('active');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [linkedEvents, setLinkedEvents] = useState<Record<string, ArbitiumThreatEvent[]>>({});
  const [loadingEventId, setLoadingEventId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const loadTests = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const data = await getTestData();
      setTests(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setTests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTests();
  }, [loadTests]);

  const filteredTests =
    filterMode === 'active'
      ? tests.filter((t) => (t.status || '') === 'in_progress')
      : tests.filter((t) => (t.status || '') !== 'in_progress');

  const handleCheck = async (testId: string) => {
    setActionLoading(testId);
    try {
      const res = await checkTest(testId);
      const ok = res?.success === true;
      if (ok) {
        notify({ tone: 'success', message: 'Check completed' });
        await loadTests();
      } else {
        const msg = (res?.output as any)?.[0]?.error || (res as any)?.error || 'Check failed';
        notifyError(msg);
      }
    } catch (e) {
      notifyError(e instanceof Error ? e.message : String(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (testId: string) => {
    setActionLoading(testId);
    try {
      const res = await abortTest(testId);
      const ok = res?.success === true;
      if (ok) {
        notify('Test cancelled');
        await loadTests();
      } else {
        const msg = (res?.output as any)?.[0]?.error || (res as any)?.error || 'Cancel failed';
        notifyError(msg);
      }
    } catch (e) {
      notifyError(e instanceof Error ? e.message : String(e));
    } finally {
      setActionLoading(null);
    }
  };

  const handleCleanup = async (testId: string) => {
    setActionLoading(testId);
    try {
      const res = await cleanupTest(testId);
      const ok = res?.success === true;
      if (ok) {
        notify({ tone: 'success', message: 'Cleanup completed' });
        await loadTests();
      } else {
        const msg = (res?.output as any)?.[0]?.error || (res as any)?.error || 'Cleanup failed';
        notifyError(msg);
      }
    } catch (e) {
      notifyError(e instanceof Error ? e.message : String(e));
    } finally {
      setActionLoading(null);
    }
  };

  const loadLinkedEvents = useCallback(async (test: ArbitiumTest) => {
    const cid = (test.candidate_id || '').trim();
    if (!cid || linkedEvents[test._id]) return;
    setLoadingEventId(test._id);
    try {
      const events = await getThreatEventsByCandidateId(cid);
      setLinkedEvents((prev) => ({ ...prev, [test._id]: events }));
    } finally {
      setLoadingEventId(null);
    }
  }, [linkedEvents]);

  const toggleExpand = (test: ArbitiumTest) => {
    if (expandedId === test._id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(test._id);
    if (test.candidate_id) loadLinkedEvents(test);
  };

  const statusBadge = (test: ArbitiumTest) => {
    const status = (test.status || '').trim();
    const result = (test.result || '').trim();
    if (status === 'in_progress') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
          <Clock className="w-3 h-3" />
          In progress
        </span>
      );
    }
    if (result === 'success') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-500/20 text-green-400 border border-green-500/30">
          <CheckCircle className="w-3 h-3" />
          Success
        </span>
      );
    }
    if (result === 'aborted') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-gray-500/20 text-gray-400 border border-gray-500/30">
          <XCircle className="w-3 h-3" />
          Aborted
        </span>
      );
    }
    if (result === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-red-500/20 text-red-400 border border-red-500/30">
          <XCircle className="w-3 h-3" />
          Failed
        </span>
      );
    }
    return <span className="text-gray-500 text-xs">—</span>;
  };

  const cleanupBadge = (test: ArbitiumTest) => {
    const cs = (test.cleanup_status || 'pending').trim();
    if (cs === 'cleaned') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-green-500/20 text-green-400">
          <CheckCircle className="w-3 h-3" />
          Cleaned
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-amber-500/20 text-amber-400">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    );
  };

  return (
    <div className="min-h-view bg-c3-bg font-c3 text-c3-white">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-arbitium-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-arbitium-accent/5 rounded-full blur-3xl" />
      </div>

      <header className="relative z-10 bg-arbitium-dark/80 backdrop-blur-xl border-b border-arbitium-primary/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <FlaskConical className="w-6 h-6 text-arbitium-accent" />
              <h1 className="text-xl font-semibold text-white">Monitor tests</h1>
            </div>
          </div>
          <button
            onClick={loadTests}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      <main className="relative z-10 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-400">Filter:</span>
            </div>
            <button
              onClick={() => setFilterMode('active')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filterMode === 'active'
                  ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                  : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10 border border-transparent'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilterMode('completed')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filterMode === 'completed'
                  ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                  : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10 border border-transparent'
              }`}
            >
              Completed
            </button>
          </div>

          {error && (
            <div className="mb-6 flex items-center gap-2 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 text-arbitium-accent animate-spin mb-4" />
              <p className="text-gray-400">Loading tests…</p>
            </div>
          ) : (
            <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-arbitium-primary/10 text-sm text-gray-500 font-medium">
                <div className="col-span-1" />
                <div className="col-span-2">Threat</div>
                <div className="col-span-2">Title</div>
                <div className="col-span-2">Status / Result</div>
                <div className="col-span-1">Cleanup</div>
                <div className="col-span-2">Created</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {filteredTests.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No tests found for the selected filter.
                </div>
              ) : (
                <div className="divide-y divide-arbitium-primary/10">
                  {filteredTests.map((test) => (
                    <React.Fragment key={test._id}>
                      <div className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-arbitium-primary/5 transition-colors items-center">
                        <div className="col-span-1">
                          <button
                            onClick={() => toggleExpand(test)}
                            className="p-1 text-gray-400 hover:text-white rounded"
                            title={test.candidate_id ? 'View linked threat event' : 'No linked event'}
                          >
                            {test.candidate_id ? (
                              expandedId === test._id ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )
                            ) : (
                              <span className="w-4 h-4 inline-block" />
                            )}
                          </button>
                        </div>
                        <div className="col-span-2 text-sm text-white font-mono">
                          {test.threat || '—'}
                        </div>
                        <div className="col-span-2 text-sm text-gray-300 truncate" title={test.title}>
                          {test.title || '—'}
                        </div>
                        <div className="col-span-2">{statusBadge(test)}</div>
                        <div className="col-span-1">{cleanupBadge(test)}</div>
                        <div className="col-span-2 text-sm text-gray-400">
                          {formatIsoDate(test.created_at)}
                        </div>
                        <div className="col-span-2 flex items-center justify-end gap-2">
                          {test.status === 'in_progress' && (
                            <>
                              <button
                                onClick={() => handleCheck(test._id)}
                                disabled={!!actionLoading}
                                className="p-2 text-gray-400 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors disabled:opacity-50"
                                title="Check test"
                              >
                                {actionLoading === test._id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-4 h-4" />
                                )}
                              </button>
                              <button
                                onClick={() => handleCancel(test._id)}
                                disabled={!!actionLoading}
                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                                title="Cancel test"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleCleanup(test._id)}
                            disabled={!!actionLoading}
                            className="p-2 text-gray-400 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="Cleanup resources"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {expandedId === test._id && (
                        <div className="px-4 py-3 bg-arbitium-darker/50 border-t border-arbitium-primary/10">
                          {loadingEventId === test._id ? (
                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Loading linked threat event…
                            </div>
                          ) : (linkedEvents[test._id]?.length ?? 0) > 0 ? (
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 text-sm text-arbitium-accent font-medium">
                                <Shield className="w-4 h-4" />
                                Linked threat event(s)
                              </div>
                              {linkedEvents[test._id].map((ev) => (
                                <div
                                  key={ev._id}
                                  className="rounded-lg p-3 bg-arbitium-dark/50 border border-arbitium-primary/20 text-sm"
                                >
                                  <div className="flex flex-wrap gap-4 text-gray-300">
                                    <span>
                                      <strong className="text-gray-400">ID:</strong> {ev._id}
                                    </span>
                                    {ev.started_at && (
                                      <span>
                                        <strong className="text-gray-400">Started:</strong>{' '}
                                        {formatIsoDate(ev.started_at)}
                                      </span>
                                    )}
                                    {ev.finished_at && (
                                      <span>
                                        <strong className="text-gray-400">Finished:</strong>{' '}
                                        {formatIsoDate(ev.finished_at)}
                                      </span>
                                    )}
                                    {ev.is_test !== undefined && (
                                      <span>
                                        <strong className="text-gray-400">Is test:</strong>{' '}
                                        {ev.is_test ? 'Yes' : 'No'}
                                      </span>
                                    )}
                                    {Array.isArray(ev.remediations) && (
                                      <span>
                                        <strong className="text-gray-400">Remediations:</strong>{' '}
                                        {ev.remediations.length}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : test.candidate_id ? (
                            <p className="text-sm text-gray-500">No threat event linked yet.</p>
                          ) : null}
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
